import * as React from "react";

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const pad = (value) => String(value).padStart(2, "0");

const plural = (count, unit) => `${count} ${unit}${count === 1 ? "" : "s"}`;

/**
 * Time left until `endAt`, measured on the server's clock (`clockOffsetMs` is
 * serverTime - Date.now() at fetch time), so a wrong device clock does not
 * matter. Ticks every second on the last day and every 30s before that.
 *
 * `announcement` changes at most once a minute and is meant for an
 * aria-live region; `label` is the visual text and may change every second.
 */
export function useOfferCountdown(endAt, clockOffsetMs = 0) {
  const endMs = new Date(endAt).getTime();
  const hasEnd = Number.isFinite(endMs);
  const [now, setNow] = React.useState(() => Date.now() + clockOffsetMs);

  React.useEffect(() => {
    if (!hasEnd) return undefined;

    let timer;
    const tick = () => {
      const current = Date.now() + clockOffsetMs;
      setNow(current);

      const remaining = endMs - current;
      if (remaining <= 0) return;

      const delay = remaining <= DAY ? 1000 - (current % 1000) : Math.min(30 * 1000, remaining - DAY);
      timer = window.setTimeout(tick, Math.max(delay, 250));
    };

    tick();
    return () => window.clearTimeout(timer);
  }, [endMs, hasEnd, clockOffsetMs]);

  if (!hasEnd) {
    return { remainingMs: null, isExpired: false, isLastDay: false, label: "", announcement: "" };
  }

  const remainingMs = Math.max(0, endMs - now);
  const isLastDay = remainingMs > 0 && remainingMs <= DAY;

  const days = Math.floor(remainingMs / DAY);
  const hours = Math.floor((remainingMs % DAY) / HOUR);
  const minutes = Math.floor((remainingMs % HOUR) / MINUTE);
  const seconds = Math.floor((remainingMs % MINUTE) / 1000);

  const label = isLastDay
    ? `Last day! Ends in ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `Ends in ${days}d ${hours}h ${pad(minutes)}m`;

  const announcement = remainingMs <= 0
    ? "Offer has ended"
    : `Offer ends in ${[
        days > 0 && plural(days, "day"),
        hours > 0 && plural(hours, "hour"),
        plural(minutes, "minute"),
      ].filter(Boolean).join(" ")}`;

  return { remainingMs, isExpired: remainingMs <= 0, isLastDay, label, announcement };
}
